// OrderDetailsPage.tsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Card,
  CardMedia,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Snackbar,
  Fade,
  Skeleton,
  Container,
  CardContent,
} from "@mui/material";
import { Link, useNavigate, useParams } from "react-router-dom";
import { connect, useDispatch, useSelector } from "react-redux";
import {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
  sendOrderProposal,
  markOrderAsDone,
  completeOrder,
} from "./actions";
import {
  OrderViewModel,
  OrderAttributeAnswerViewModel,
  OrderProposalViewModel,
  OrderStatus,
} from "../../configuration/api/apiSchema";
import ChatIcon from "@mui/icons-material/Chat";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoIcon from "@mui/icons-material/Info";
import DescriptionIcon from "@mui/icons-material/Description";
import CategoryIcon from "@mui/icons-material/Category";
import HandymanIcon from "@mui/icons-material/Handyman";
import { HandymanAppState } from "../../configuration/redux/reducers";
import PreviewImageModal from "../../components/PreviewImage/PreviewImageModal";
import RateReviewIcon from "@mui/icons-material/RateReview";
import StarIcon from "@mui/icons-material/Star";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreateReviewForm from "../../components/Reviews/CreateReviewForm";
import ReviewsList from "../../components/Reviews/ReviewsList";
import CloseIcon from "@mui/icons-material/Close";
import HandshakeIcon from "@mui/icons-material/Handshake";

// NEW: BrowseHandyman (similar to CreateOrderPage approach)
import BrowseHandymanList from "../BrowseHandyman/BrowseHandymanList";
import GoogleMapsWrapper from '../UserProfile/components/UserDetails/GoogleMapsWrapper';
import { createOrGetChat } from "./actions";
import { createPaymentIntent, createCheckoutSession } from "./actions";
import { getOrderReviews, createReview } from "../Reviews/actions";
import { CreateReviewCommand, ReviewType } from "../../configuration/api/apiSchema";
import StripeCheckoutForm from "../Stripe/StripeCheckoutForm";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from '@stripe/stripe-js';
import { useSearchParams } from 'react-router-dom';

const stripePromise = loadStripe("pk_live_51RBePuJM47EWuzCeb9b1nafOzreaIyJLlMdnPbaWjaDNUQVjhQudPoggoMXyc533NLoMDHRCGKA0AY5FB7sHzFdp00KZJCT3Nx");


export const mapOrderStatusToString = (status?: OrderStatus): string => {
  switch (status) {
    case OrderStatus.New:
      return "New";
    case OrderStatus.InProgress:
      return "In Progress";
    case OrderStatus.MarkAsDone:
      return "Marked as Done";
    case OrderStatus.Completed:
      return "Completed";
    default:
      return "Unknown Status";
  }
};

interface StateFromProps {
  orderDetails: OrderViewModel | null;
  orderDetailsLoading: boolean;
  orderDetailsError: string | null;
  orderProposals: OrderProposalViewModel[];
  orderProposalsLoading: boolean;
  role: string; // 'customer' or 'handyman'
  currentUserId?: string;
  orderReviews: any[];
  orderReviewsLoading: boolean;
  orderReviewsTotalCount: number;
  orderReviewsError: string | null;
  currentPage: number;
}

interface DispatchFromProps {
  getOrderDetails: (orderId: string) => void;
  getOrderProposals: (orderId: string) => void;
  updateOrderProposal: (
    orderProposalId: string,
    status: "Confirmed" | "Rejected"
  ) => void;
  sendOrderProposal: (orderId: string, handymanId: string) => void;
  getOrderReviews: (orderId: string) => void;
}

type Props = StateFromProps & DispatchFromProps;

const OrderDetailsPage: React.FC<Props> = (props: Props) => {
  const { orderId } = useParams<{ orderId: string }>();
  
  const [searchParams, setSearchParams] = useSearchParams();

  const sessionId = searchParams.get('session_id');
  const canceled = searchParams.get('canceled');

  const {
    orderDetails,
    orderDetailsLoading,
    orderDetailsError,
    orderProposals,
    orderProposalsLoading,
    role,
    currentUserId,
    orderReviews,
    orderReviewsLoading,
    orderReviewsTotalCount,
    orderReviewsError,
    currentPage,
  } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | undefined>();

  const handlePayNow = async () => {
    if (!orderId) return;
  
    try {
      // 1. Create Checkout Session
      const response = await dispatch(createCheckoutSession(orderId) as any);
  
      if (!response.sessionId) {
        throw new Error("Session ID not returned");
      }
  
      // 2. Redirect to Stripe Checkout
      const stripe = await stripePromise;
      if (!stripe) {
        throw new Error("Stripe.js failed to load");
      }
  
      await stripe.redirectToCheckout({
        sessionId: response.sessionId,
      });
  
    } catch (error) {
      console.error("Failed to create checkout session", error);
      openSnackbar("Failed to start checkout", "error");
    }
  };

  const handleChatWithHandyman = async (
    e: React.MouseEvent,
    handymanId: string
  ) => {
    e.stopPropagation(); // prevent parent container from also firing
    if (!currentUserId) return;

    try {
      // Wait for the createOrGetChat call to complete
      const chatId: any = await dispatch(createOrGetChat([currentUserId, handymanId]) as any);

      // If it returned a valid chat object with an "id", then navigate to that chat
      if (chatId) {
        navigate(`/chats/${chatId}`);
      }
    } catch (error) {
      // Handle any errors if needed (e.g. show a toast)
      console.error("Failed to create or get chat:", error);
    }
  };

  useEffect(() => {
    if (sessionId) {
      openSnackbar('✅ Payment completed successfully!', 'success');
      if (orderId) {
        (dispatch as any)(getOrderDetails(orderId));
      }
      // Clean up URL after success
      setTimeout(() => {
        searchParams.delete('session_id');
        navigate(`/orders/${orderId}`, { replace: true });
      }, 1000); // 1s delay to allow snackbar to show
    }
    if (canceled) {
      openSnackbar('❌ Payment was canceled.', 'error');
      // Clean up URL after cancel
      setTimeout(() => {
        searchParams.delete('canceled');
        navigate(`/orders/${orderId}`, { replace: true });
      }, 1000);
    }
  }, [sessionId, canceled, orderId]);

  useEffect(() => {
    if (orderId) {
      props.getOrderDetails(orderId);
    }
  }, [orderId]);

  // NEW: get proposals if user is customer or handyman
  useEffect(() => {
    if (orderId && (role === "customer" || role === "handyman")) {
      props.getOrderProposals(orderId);
    }
  }, [orderId, role]);

  // Modal for previewing images
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const handleImageClick = (base64Image: string) => {
    setSelectedImage(base64Image);
    setIsImageModalOpen(true);
  };
  const handleCloseImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedImage(null);
  };

  // Modal for "select a handyman"
  const [browseHandymanOpen, setBrowseHandymanOpen] = useState(false);
  const handleOpenBrowseHandyman = () => setBrowseHandymanOpen(true);
  const handleCloseBrowseHandyman = () => setBrowseHandymanOpen(false);

  // Approve / Reject actions
  const handleApproveProposal = (proposalId: string) => {
    props.updateOrderProposal(proposalId, "Confirmed");
  };
  const handleRejectProposal = (proposalId: string) => {
    props.updateOrderProposal(proposalId, "Rejected");
  };

  const handleSelectHandyman = async (handymanId: string) => {
    if (!orderId) return;

    try {
      if (orderId) {
        await dispatch(sendOrderProposal(orderId, handymanId) as any);
      }
      handleCloseBrowseHandyman();

      openSnackbar("Proposal sent!", "success");

      await dispatch(getOrderDetails(orderId) as any);
      await dispatch(getOrderProposals(orderId) as any);
    } catch (err) {
      openSnackbar("Failed to send proposal", "error");
    }
  };

  // Renders additional order attribute answers
  const renderOrderAttributes = (answers: OrderAttributeAnswerViewModel[]) => {
    return answers.map((ans, idx) => {
      const propertyName =
        ans.categoryAttribute?.translatedAtributeContent?.label || "Property";
      const value = ans.value || "N/A";
      return (
        <Typography key={idx} variant="body1" sx={{ mt: 1 }}>
          <strong>{propertyName}:</strong> {value}
        </Typography>
      );
    });
  };

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<
    "success" | "error"
  >("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // 2) Helpers to open/close
  const openSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // 3) Example: Mark as Done (Handyman)
  const handleMarkAsDone = async () => {
    if (!orderId) return;

    try {
      // Dispatch your markOrderAsDone Redux thunk
      await dispatch(markOrderAsDone(orderId) as any);
      openSnackbar("Order has been marked as done!", "success");
    } catch (err) {
      openSnackbar("Failed to mark order as done", "error");
    }
  };

  // 4) Example: Complete Order (Customer)
  const handleCompleteOrder = async () => {
    if (!orderId) return;

    try {
      // Dispatch your completeOrder Redux thunk
      await dispatch(completeOrder(orderId) as any);
      openSnackbar("Order is now completed!", "success");
    } catch (err) {
      openSnackbar("Failed to complete order", "error");
    }
  };

  function getHandymanSection(): React.ReactNode {
    if (!orderDetails) {
      return;
    }

    return <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Handyman
      </Typography>
      {orderDetails.handyman ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            src={
              orderDetails.handyman.avatarUrl ||
              "https://via.placeholder.com/40"
            }
            alt={`${orderDetails.handyman.firstName} ${orderDetails.handyman.lastName}`}
            sx={{ width: 30, height: 30 }}
          />
          <Typography variant="body1">
            {orderDetails.handyman.firstName +
              " " +
              orderDetails.handyman.lastName}
          </Typography>
          <IconButton
            color="primary"
            onClick={(e) => handleChatWithHandyman(e, orderDetails.handyman?.id ?? "")}
            title="Chat with Handyman"
          >
            <ChatIcon />
          </IconButton>
        </Box>
      ) : (
        <Typography variant="body1" color="text.secondary">
          Not Selected
        </Typography>
      )}

      {!orderDetails.handyman && (
        <Box mt={2}>
          <Button variant="outlined" onClick={handleOpenBrowseHandyman}>
            Select Handyman
          </Button>
        </Box>
      )}
    </Box>
  }
  function getCustomerSection(): React.ReactNode {
    if (!orderDetails?.customer) {
      return;
    }

    return <Box mt={4}>
      <Typography variant="h6" gutterBottom>
        Customer
      </Typography>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Avatar
          src={
            orderDetails.customer.avatarUrl ||
            "https://via.placeholder.com/40"
          }
          alt={`${orderDetails.customer.firstName} ${orderDetails.customer.lastName}`}
          sx={{ width: 30, height: 30 }}
        />
        <Typography variant="body1">
          {orderDetails.customer.firstName +
            " " +
            orderDetails.customer.lastName}
        </Typography>
        <IconButton
          color="primary"
          onClick={(e) => handleChatWithHandyman(e, orderDetails.customer?.id ?? "")}
          title="Chat with customer"
        >
          <ChatIcon />
        </IconButton>
      </Box>
    </Box>
  }

  useEffect(() => {
    if (orderId) {
      dispatch(getOrderReviews(orderId) as any);
    }
  }, [dispatch, orderId]);

  const getAverageRating = () => {
    if (!orderReviews || orderReviews.length === 0) return 0;
    const sum = orderReviews.reduce((acc, review) => acc + (review.rating || 0), 0);
    return sum / orderReviews.length;
  };

  const [showCreateReview, setShowCreateReview] = useState(false);

  const getReviewsSection = () => {
    if (!orderDetails) return null;

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Reviews</Typography>
          <Button
            component={Link}
            to={`/reviews/order/${orderId}`}
            variant="outlined"
            color="primary"
          >
            View All Reviews ({orderReviewsTotalCount})
          </Button>
        </Box>
        <ReviewsList
          reviews={orderReviews.slice(0, 3)}
          loading={orderReviewsLoading}
          error={orderReviewsError}
          totalCount={orderReviewsTotalCount}
          showActions={false}
          emptyMessage="No reviews yet"
          title=""
          currentPage={1}
          onPageChange={() => {}}
        />
      </Box>
    );
  };

  const canLeaveReview = () => {
    if (!currentUserId || !orderDetails) return false;
    
    // Only completed orders can be reviewed
    if (orderDetails.status !== OrderStatus.Completed) return false;
    
    // Check if user is part of this order
    const isCustomer = currentUserId === orderDetails.customer?.id;
    const isHandyman = currentUserId === orderDetails.handyman?.id;
    
    if (!isCustomer && !isHandyman) return false;
    
    // Check if user has already left a review
    const hasReviewed = orderReviews.some(review => 
      review.reviewer?.id === currentUserId
    );
    
    return !hasReviewed;
  };

  const getRevieweeId = () => {
    if (!currentUserId || !orderDetails) return "";
    
    const isCustomer = currentUserId === orderDetails.customer?.id;
    return isCustomer 
      ? orderDetails.handyman?.id || ""
      : orderDetails.customer?.id || "";
  };

  const getReviewType = () => {
    if (!currentUserId || !orderDetails) return ReviewType.CustomerToHandyman;
    
    const isCustomer = currentUserId === orderDetails.customer?.id;
    return isCustomer 
      ? ReviewType.CustomerToHandyman 
      : ReviewType.HandymanToCustomer;
  };

  const handleSubmitReview = async (data: CreateReviewCommand) => {
    await dispatch(createReview(data) as any);
    setShowCreateReview(false);
    // Refresh reviews
    if (orderId) {
      dispatch(getOrderReviews(orderId, currentPage) as any);
    }
  };

  const handlePageChange = (page: number) => {
    if (orderId) {
      dispatch(getOrderReviews(orderId, page) as any);
    }
  };

  const getProposalsSection = () => {
    if (!orderDetails || (role !== "customer" && role !== "handyman")) return null;

    return (
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Proposals</Typography>
          <Button
            component={Link}
            to={`/proposals/order/${orderId}`}
            variant="outlined"
            color="primary"
            startIcon={<HandshakeIcon />}
          >
            View All Proposals ({orderProposals.length})
          </Button>
        </Box>
        {orderProposalsLoading ? (
          <Box display="flex" justifyContent="center" p={2}>
            <CircularProgress size={24} />
          </Box>
        ) : orderProposals.length === 0 ? (
          <Typography color="text.secondary" p={2}>No proposals yet</Typography>
        ) : (
          <Box>
            {orderProposals.slice(0, 2).map((proposal) => {
              const isSentByCurrentUser =
                (proposal.isInitiatedByCustomer && currentUserId === proposal.customerId) ||
                (!proposal.isInitiatedByCustomer && currentUserId === proposal.handymanId);
              
              return (
                <Box
                  key={proposal.id}
                  p={2}
                  mb={1}
                  sx={{ backgroundColor: "#f9f9f9", borderRadius: 2 }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                      <HandymanIcon color="action" sx={{ mr: 1 }} />
                      <Typography variant="subtitle1">
                        {proposal?.handyman
                          ? `${proposal.handyman.firstName} ${proposal.handyman.lastName}`
                          : "N/A"}
                      </Typography>
                    </Box>
                    <Chip
                      label={isSentByCurrentUser ? "Sent" : "Received"}
                      color={isSentByCurrentUser ? "primary" : "secondary"}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    Status: {proposal.status}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    );
  };

  // Add this component for the skeleton loader
  const OrderDetailsSkeleton = () => (
    <Grid container spacing={4}>
      {/* Left Column */}
      <Grid item xs={12} md={8}>
        {/* Category Header */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Skeleton variant="circular" width={40} height={40} sx={{ mr: 2 }} />
          <Box>
            <Skeleton variant="text" width={60} height={20} />
            <Skeleton variant="text" width={120} height={24} />
          </Box>
        </Box>

        {/* Order Details Grid */}
        <Grid container spacing={2}>
          {/* Name */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={60} height={20} />
                <Skeleton variant="text" width={140} height={24} />
              </Box>
            </Box>
          </Grid>
          {/* Description */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={80} height={20} />
                <Skeleton variant="text" width={160} height={24} />
              </Box>
            </Box>
          </Grid>
          {/* Price */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={50} height={20} />
                <Skeleton variant="text" width={80} height={24} />
              </Box>
            </Box>
          </Grid>
          {/* Created At */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={70} height={20} />
                <Skeleton variant="text" width={150} height={24} />
              </Box>
            </Box>
          </Grid>
          {/* Due Date */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={40} height={20} />
                <Skeleton variant="text" width={150} height={24} />
              </Box>
            </Box>
          </Grid>
          {/* Status */}
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Skeleton variant="circular" width={24} height={24} sx={{ mr: 2 }} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width={50} height={20} />
                <Skeleton variant="text" width={100} height={24} />
              </Box>
              <Skeleton variant="rectangular" width={120} height={36} sx={{ ml: 10, borderRadius: 1 }} />
            </Box>
          </Grid>
        </Grid>

        {/* Additional Information */}
        <Box mt={4}>
          <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
          {[1, 2].map((i) => (
            <Skeleton key={i} variant="text" width="70%" height={24} sx={{ mb: 1 }} />
          ))}
        </Box>

        {/* Handyman/Customer Section */}
        <Box mt={4}>
          <Skeleton variant="text" width={100} height={32} sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton variant="circular" width={30} height={30} />
            <Skeleton variant="text" width={150} height={24} />
            <Skeleton variant="circular" width={36} height={36} />
          </Box>
        </Box>

        {/* Order Photos */}
        <Box mt={4}>
          <Skeleton variant="text" width={120} height={32} sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={6} sm={4} md={3} key={i}>
                <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 1 }} />
              </Grid>
            ))}
          </Grid>
        </Box>
      </Grid>

      {/* Right Column */}
      <Grid item xs={12} md={4}>
        <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
          {/* Payment & Pricing */}
          <Skeleton variant="text" width={180} height={32} sx={{ mb: 2 }} />
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ mb: 2 }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={24} />
          </Box>
          <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 1, mb: 3 }} />

          {/* Proposals Section */}
          <Box mt={3}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Skeleton variant="text" width={100} height={32} />
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
            </Box>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ mb: 2, p: 2, bgcolor: "#f9f9f9", borderRadius: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Skeleton variant="circular" width={24} height={24} sx={{ mr: 1 }} />
                    <Skeleton variant="text" width={120} height={24} />
                  </Box>
                  <Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} />
                </Box>
                <Skeleton variant="text" width="40%" height={20} />
              </Box>
            ))}
          </Box>

          {/* Reviews Section */}
          <Box mt={3}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
              <Skeleton variant="text" width={80} height={32} />
              <Skeleton variant="rectangular" width={120} height={36} sx={{ borderRadius: 1 }} />
            </Box>
            {[1, 2].map((i) => (
              <Box key={i} sx={{ mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Skeleton variant="circular" width={40} height={40} sx={{ mr: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Skeleton variant="text" width="40%" height={24} />
                    <Skeleton variant="text" width="20%" height={20} />
                  </Box>
                </Box>
                <Skeleton variant="text" width="90%" height={20} />
                <Skeleton variant="text" width="70%" height={20} />
              </Box>
            ))}
          </Box>
        </Paper>
      </Grid>
    </Grid>
  );

  return (
    <Box
      sx={{
        background: "linear-gradient(to bottom, #f5f5f5, #ffffff)",
        minHeight: "100vh",
        py: 10,
      }}
    >
      <Box
        width={{ xs: "90%", sm: "80%", md: "75%" }}
        mx="auto"
        mt={4}
        p={{ xs: 2, sm: 4 }}
        component={Paper}
        elevation={4}
        sx={{ borderRadius: 4 }}
      >
        {/* Image Preview Modal */}
        <PreviewImageModal
          base64SelectedImage={selectedImage}
          isImageModalOpen={isImageModalOpen}
          handleCloseImageModal={handleCloseImageModal}
        />

        {/* Browse Handyman Modal */}
        <Dialog
          open={browseHandymanOpen}
          onClose={handleCloseBrowseHandyman}
          fullWidth
          maxWidth="md"
        >
          <DialogTitle>Select a Handyman</DialogTitle>
          <DialogContent>
          <GoogleMapsWrapper>
            <BrowseHandymanList
              preselectedCategoryKey={orderDetails?.category?.key ?? undefined}
              excludeFilters={["price-range", "location", "name"]}
              actionButtonTitle="Select"
              onAction={handleSelectHandyman}
            />
          </GoogleMapsWrapper>
          </DialogContent>
        </Dialog>

        {/* Create Review Dialog */}
        <Dialog
          open={showCreateReview}
          onClose={() => setShowCreateReview(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { height: "45vh", maxHeight: "45vh" } }}
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Write a Review</Typography>
              <IconButton onClick={() => setShowCreateReview(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <CreateReviewForm
              orderId={orderId || ""}
              reviewerId={currentUserId || ""}
              revieweeId={getRevieweeId()}
              reviewType={getReviewType()}
              onSubmit={handleSubmitReview}
              onCancel={() => setShowCreateReview(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Page Content */}
        {orderDetailsLoading ? (
          <OrderDetailsSkeleton />
        ) : orderDetailsError ? (
          <Alert severity="error">{orderDetailsError}</Alert>
        ) : orderDetails ? (
          <Grid container spacing={4}>
            {/* Left Column */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>
                  <CategoryIcon />
                </Avatar>


                <Snackbar
                  open={snackbarOpen}
                  autoHideDuration={6000}
                  onClose={handleCloseSnackbar}
                  anchorOrigin={{ vertical: "top", horizontal: "center" }}
                >
                  <Alert
                    severity={snackbarSeverity}
                    onClose={handleCloseSnackbar}
                    sx={{ width: "100%" }}
                  >
                    {snackbarMessage}
                  </Alert>
                </Snackbar>

                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Category
                  </Typography>
                  <Typography variant="body1">
                    {orderDetails.category?.name || "N/A"}
                  </Typography>
                </Box>
              </Box>

              {/* Title / Description */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <InfoIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Name
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.name ||
                          orderDetails.category?.name ||
                          "Order Title"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <DescriptionIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Description
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.description || "No description provided."}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Price */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AttachMoneyIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Price
                      </Typography>
                      <Typography variant="body1">
                        {`$${orderDetails.price ?? "N/A"}`}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Created / Due */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <CalendarTodayIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Created At
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.createdAtUtc
                          ? new Date(orderDetails.createdAtUtc).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <AccessTimeIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Due
                      </Typography>
                      <Typography variant="body1">
                        {orderDetails.dueDateUtc
                          ? new Date(orderDetails.dueDateUtc).toLocaleString()
                          : "N/A"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                {/* Status */}
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <InfoIcon color="action" sx={{ mr: 2 }} />
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        Status
                      </Typography>
                      <Typography variant="body1">
                        {mapOrderStatusToString(orderDetails.status)}
                      </Typography>
                    </Box>

                    <Box marginLeft={10}>
                      {role === "handyman" && orderDetails?.status === "InProgress" && (
                        <Button variant="contained" color="success" onClick={handleMarkAsDone}>
                          Mark as Done
                        </Button>
                      )}

                      {role === "customer" && orderDetails?.status === "MarkAsDone" && (
                        <Button variant="contained" color="success" onClick={handleCompleteOrder}>
                          Complete Order
                        </Button>
                      )}
                    </Box>
                  </Box>

                </Grid>
              </Grid>

              {/* Additional Information */}
              {orderDetails.orderAttributeAnswers &&
                orderDetails.orderAttributeAnswers.length > 0 && (
                  <Box mt={4}>
                    <Typography variant="h6" gutterBottom>
                      Additional Information
                    </Typography>
                    {renderOrderAttributes(orderDetails.orderAttributeAnswers)}
                  </Box>
                )}

              {/* Handyman Section */}
              {role === "customer" && getHandymanSection()}

              {/* Customer Section */}
              {role === "handyman" && getCustomerSection()}

              {/* Order Photos */}
              <Divider sx={{ my: 4 }} />
              <Box>
                <Typography variant="h6" gutterBottom>
                  Order Photos
                </Typography>
                {orderDetails.base64Images &&
                  orderDetails.base64Images.length > 0 ? (
                  <Grid container spacing={2}>
                    {orderDetails.base64Images.map((src, idx) => (
                      <Grid item xs={6} sm={4} md={3} key={idx}>
                        <Card
                          sx={{ cursor: "pointer" }}
                          onClick={() => handleImageClick(src)}
                        >
                          <CardMedia
                            component="img"
                            image={src}
                            alt={`Order Photo ${idx + 1}`}
                          />
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No images available.
                  </Typography>
                )}
              </Box>
            </Grid>
            
            {/* Right Column: Payment & Pricing */}
            {role === "customer" && orderDetails.status === OrderStatus.Completed && (
              <Grid item xs={12} md={4}>
                <Paper
                  elevation={3}
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    background: "linear-gradient(135deg, #fdfbfb 0%, #ebedee 100%)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    Payment & Pricing
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body1">
                      <strong>Price:</strong> ${orderDetails.price ?? "N/A"}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Due Date:</strong>{" "}
                      {orderDetails.dueDateUtc
                        ? new Date(orderDetails.dueDateUtc).toLocaleString()
                        : "N/A"}
                    </Typography>
                  </Box>

                  {orderDetails.paymentStatus === "Pending" || orderDetails.paymentStatus === "PaymentInitiated" ? (
                    <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handlePayNow}
                    sx={{
                      background: "linear-gradient(90deg, #1e3c72 0%, #2a5298 100%)",
                      color: "#fff",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      textTransform: "none",
                      padding: "12px 24px",
                      borderRadius: "8px",
                      boxShadow: "0 4px 10px rgba(30, 60, 114, 0.3)",
                      '&:hover': {
                        background: "linear-gradient(90deg, #2a5298 0%, #1e3c72 100%)",
                      },
                    }}
                  >
                    💳 Pay Now
                  </Button>
                  ) : orderDetails.paymentStatus === "Processing" ? (
                    <Button
                    variant="outlined"
                    color="warning"
                    fullWidth
                    disabled
                    sx={{ fontWeight: 600, borderRadius: "8px", py: 1.5 }}
                  >
                    ⏳ Payment In Process
                  </Button>
                  ) : orderDetails.paymentStatus === "Paid" ? (
                    <Alert severity="success" icon={false} sx={{ fontWeight: 600, borderRadius: 2 }}>
                    ✅ Payment Successfully Paid
                  </Alert>
                  ) : (
                    <Alert severity="info">Payment status: {orderDetails.paymentStatus}</Alert>
                  )}

                  {showPaymentForm && clientSecret && (
                    <Box mt={3}>
                      <Elements
                      stripe={stripePromise}
                      options={{
                        clientSecret,
                        appearance: { theme: 'flat' },
                        loader: 'auto',
                      }}
                    >
                      <StripeCheckoutForm
                        clientSecret={clientSecret}
                        amount={orderDetails.price ?? 0} // 👈 Pass your order amount here
                        onSuccess={() => {
                          setShowPaymentForm(false);
                          dispatch(getOrderDetails(orderId!) as any);
                        }}
                      />
                    </Elements>
                    </Box>
                  )}
                  <Box mt={3}>{getProposalsSection()}</Box>
                  <Box mt={3}>{getReviewsSection()}</Box>
                </Paper>
              </Grid>
            )}
          </Grid>
        ) : (
          <Typography variant="h6" color="text.secondary">
            Order not found.
          </Typography>
        )}
      </Box>
    </Box>
  );
};

const mapStateToProps = (state: HandymanAppState): StateFromProps => ({
  orderDetails: state.order.orderDetails,
  orderDetailsLoading: state.order.orderDetailsLoading,
  orderDetailsError: state.order.orderDetailsError,
  orderProposals: state.order.orderProposals,
  orderProposalsLoading: state.order.orderProposalsLoading,
  role: state.user?.authData?.role ?? "customer",
  currentUserId: state.user?.currentUserDetails?.id, // current user's id
  orderReviews: state.review.orderReviews,
  orderReviewsLoading: state.review.orderReviewsLoading,
  orderReviewsTotalCount: state.review.orderReviewsTotalCount,
  orderReviewsError: state.review.orderReviewsError,
  currentPage: state.review.currentPage,
});

export default connect(mapStateToProps, {
  getOrderDetails,
  getOrderProposals,
  updateOrderProposal,
  sendOrderProposal,
  getOrderReviews,
})(OrderDetailsPage);
